import React, { useEffect, useRef, useState, useMemo, memo } from 'react';
import { Card, Icon } from 'tea-component';
import classNames from 'classnames';
import SvgIcon from '@home/components/SvgIcon';
import { Collapse } from '@/pages/aigc/components/Collapse';
import { TStreaming } from '../../../CopilotScrollBar';
import { MainContentCmp } from '@/pages/aigc/pages/Agent/ChatPage/components/SubComp';
import { ThinkModeTextCmp } from '@/pages/aigc/pages/Agent/ChatPage/components/SubComp/ThinkModeTextCmp';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import Answer from '@/pages/aigc/pages/Agent/svg/answer-icon.svg';
import './index.scss';
import { useIntl } from 'umi';
import { useSessionId } from '@/pages/aigc/pages/Agent/ChatPage/hooks/useAgentModalSata';
import { useAccordionData } from '@/pages/aigc/pages/Agent/ChatPage/hooks/useAccordionData';
import { SwitchAgentAccordion } from '@/pages/aigc/components/SwitchAgentAccordion';
import { useLocalState } from '@/pages/aigc/pages/Agent/ChatPage/hooks/useLocalState';
import AnswerLoading from '../../../AnswerLoading';
import { isEmpty } from 'lodash-es';
import CommonBubble from '@home/components/CommonBubble';
import BasicImage from '@home/components/BasicImage';
import {
  TokenTotalCmp,
} from '@/pages/aigc/pages/Agent/ChatPage/components/SubComp/TokenTotalCmp';
import { shouldSkipThinkCmpRender } from '@/pages/aigc/pages/Agent/ChatPage/components/SubComp/thinkCmpMemo';
import { useAgentModelsData } from '../../../../../AgentCollection/hooks';
import type { IAgentData } from '../../../../../AgentCollection/types';
import { LANGUAGE_CN } from '@home/_config/config';
import { SKILL_AGENT_UUID } from '@/pages/aigc/pages/Agent/ChatPage/utils/submitHelpers';
export type TThinkCmpProps = {
  value?: Array<{
    type: string;
    value?: unknown;
    new_agent?: string;
    input?: string;
    inputIndex?: number;
  }>;
  status?: TStreaming; // 添加status属性类型定义
  isLastQA?: boolean;
  streamingState?: string;
  thinkTimeSeconds?: number;
  mode?: string;
  modeId?: string;
  answer?: Array<{ type: string; value?: unknown; }>;
  query?: string;
  messageId?: string;
};

// eslint-disable-next-line react/display-name
export const ThinkCmp = memo(
  (props: TThinkCmpProps) => {
    const {
      status,
      value,
      isLastQA,
      streamingState,
      thinkTimeSeconds,
      mode,
      modeId,
      query,
      messageId,
    } = props;
    const { sessionId } = useSessionId();
    const [isDSReportSession] = useLocalState('__dba_is_ds_report_session__', false);
    const isLoading = ['complete', 'rejected'].includes(streamingState) ? false : status === 'pending';
    const [isShowDetail, setIsShowDetail] = useState(isLoading);
    const timeRef = useRef<NodeJS.Timeout | number>(null);
    const firstFlag = useRef(false);
    // 使用 useLocalState 持久化已处理的 thinkId，避免页面切换时状态重置导致重复打点
    const [processedThinkIds, setProcessedThinkIds] = useLocalState<string[]>(
      `__dba_processed_think_ids_${messageId}__`, 
      []
    );
    const { formatMessage, locale } = useIntl();
    const { allAgents } = useAgentModelsData();
    const agentInfo = useMemo(() => {
      const agent = !modeId || !allAgents?.length ? undefined : (allAgents as IAgentData[]).find((a) => a.agentId === modeId);
      return {
        data: agent,
        name: agent ? (locale === LANGUAGE_CN ? agent.name_cn : agent.name) : '',
      };
    }, [modeId, allAgents, locale]);

    // 专门处理switch_agent类型的埋点逻辑
    useEffect(() => {
      if (!value || !streamingState) return;
      const newProcessedIds: string[] = [];
      value.forEach((item: any) => {
        if (
          item.type === 'switch_agent' &&
          !processedThinkIds.includes(item.thinkId)
        ) {
          buriedBtnPoint({
            pageId: '700501',
            buttonId: '700501011',
            extInfo: JSON.stringify({
              sessionId,
              message: query,
              agent: item?.new_agent || item?.previous_agent || '',
              from: 'copilot',
            }),
            typeId: 'aigc',
          });
          newProcessedIds.push(item.thinkId); // 标记为已处理
        }
        if (item.type === "plan_json"  && !processedThinkIds.includes(item.thinkId)) { 
          buriedBtnPoint({
            pageId: '700501',
            buttonId: '700501013',
            buttonName: '游戏名称',
            extInfo: JSON.stringify({
              sessionId,
              message: item?.buried,
              from: 'copilot'
            }),
            typeId: 'aigc',
          });
          newProcessedIds.push(item.thinkId); // 标记为已处理
        }
      });
      // 如果有新的已处理ID，更新状态
      if (newProcessedIds.length > 0) {
        setProcessedThinkIds(prev => [...prev, ...newProcessedIds]);
      }
    }, [value, query, sessionId, processedThinkIds, streamingState]);

    // 只执行一次自动关闭
    useEffect(() => {
      if (streamingState && !isLoading && isLastQA && !firstFlag.current) {
        timeRef.current = setTimeout(() => {
          setIsShowDetail(false);
          firstFlag.current = true;
        }, 2000);
      }
      return () => {
        clearTimeout(timeRef.current);
      };
    }, [isLastQA, isLoading, streamingState]);
    
    const { thinkTime, thinkModeBlock, plannerBlock, accordionData, trailingInlineItems, tokenTotalBlock } = useAccordionData({
      value,
      isLoading,
      streamingState,
      thinkTimeSeconds,
      messageId
    });
    
    const displayText = formatMessage(
      {
        id: isLoading
          ? 'dba_agent_thinking'
          : `dba_agent_thinkover_${mode || 'default'}`,
      },
      { agentName: agentInfo.name || 'AI' },
    );

    const { aiDSMode, timeVisible } = useMemo(() => {
      const isDeepThink = mode === 'deepthink';
      return {
        aiDSMode: isDeepThink,
        timeVisible: !isLoading && thinkTime,
      };
    }, [mode, isLoading, thinkTime]);

    return (
      <>
        <div className="dba-agent-cover-container dba-agent-copilot-cover-container">
          <CommonBubble content={modeId === SKILL_AGENT_UUID ? 'Skill Agent' : agentInfo.name}  overlayClassName='dba-nav-menu-overlay'>
            <span style={{ display: 'inline-flex' }}>
              <BasicImage src={agentInfo.data?.icon || Answer} errorSrc={Answer} className="dba_agent_cover" />
            </span>
          </CommonBubble>
          <Card.Body
            style={{
              padding: '0px',
            }}
          >
            <div
              className={classNames('dba-thinking', {
                ['dba-thinking-over']: !isLoading,
              })}
              onClick={() => {
                if (isLoading) return;
                setIsShowDetail(!isShowDetail);
              }}
            >
              <div style={{ marginRight: '5px' }}>
                {aiDSMode && (
                  <SvgIcon
                    type="agent-think-ds"
                    style={{ marginRight: '5px' }}
                  />
                )}
                {displayText}
                {timeVisible && <span> &nbsp;{`(${formatMessage({id: 'dba_agent_time_spent'})}${thinkTime})`}</span>}
              </div>

              {isLoading &&  <Icon type="loading" />}
              {!isLoading && (
                <div className="dba-thinking-process">
                  <SvgIcon type={isShowDetail ? 'point-top' : 'point-down'} />
                </div>
              )}
            </div>
          </Card.Body>
        </div>
        {isLoading && (
          <div className="dba-thinking-view-text">
            <SvgIcon type="alert-circle-tips" />
            {formatMessage({ id: isDSReportSession ? "dba_agent_thinking_ds" : 'dba_later_viewing_text'})}
          </div>
        )}
        {isLoading && isEmpty(value) && <AnswerLoading />}
        <Collapse
          className='dba-system-container'
          isOpen={isShowDetail}
        >
          {thinkModeBlock?.value && (
            <ThinkModeTextCmp
              value={thinkModeBlock?.value}
              streamingState={streamingState}
            />
          )}
          {plannerBlock?.value && (
            <MainContentCmp
              value={plannerBlock?.value}
              className={classNames('dbh-copilot-think-content-left dbh-copilot-think-content-planner', {
                'dba-think-content-no-top': thinkModeBlock?.value
              })}
              streamingState={streamingState}
              messageId={messageId}
            />
          )}
          {(accordionData.length > 0 || trailingInlineItems.length > 0) && (
            <SwitchAgentAccordion
              dataSource={accordionData}
              trailingInlineItems={trailingInlineItems}
              messageId={messageId}
              defaultOpenFirst={streamingState === 'pending'}
              streamingState={streamingState as string}
              isLoading={isLoading}
              iconTypeOpen="agent-point-top"
              iconTypeClose="agent-point-down"
              className="dbh-copilot-system-container"
              scrollContainerClassName=".dbh-agent-copilot .dba-chat-scroll"
              stickyMaxLines={10}
            />
          )}
          <TokenTotalCmp value={Number(tokenTotalBlock?.value)} />
        </Collapse>
      </>
    );
  },
  shouldSkipThinkCmpRender,
);
